import React, { useState, useEffect } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Grid,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Fab,
  Menu,
  MenuItem
} from '@mui/material'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { tr } from 'date-fns/locale'
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Folder as FolderIcon,
  Quiz as QuizIcon,
  Schedule as ScheduleIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  CalendarToday as CalendarIcon,
  Clear as ClearIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, addDoc, doc, setDoc, getDocs, query, where, orderBy, onSnapshot } from 'firebase/firestore'
import toast from 'react-hot-toast'

const MiniQuizCreate = ({ userData, onBack }) => {
  const { user } = useAuth()
  const [quizTitle, setQuizTitle] = useState('')
  const [quizDescription, setQuizDescription] = useState('')
  const [timeLimit, setTimeLimit] = useState('')
  const [questions, setQuestions] = useState([])
  const [unlimited, setUnlimited] = useState(true)
  const [dueDate, setDueDate] = useState(null)
  const [selectedClassId, setSelectedClassId] = useState(null)
  const [selectedClassName, setSelectedClassName] = useState(null)
  const [isIndividualSelection, setIsIndividualSelection] = useState(false)
  const [selectedStudentIds, setSelectedStudentIds] = useState([])
  const [selectedStudentNames, setSelectedStudentNames] = useState([])
  const [teacherClasses, setTeacherClasses] = useState([])
  const [students, setStudents] = useState([])
  const [showClassDialog, setShowClassDialog] = useState(false)
  const [showStudentDialog, setShowStudentDialog] = useState(false)
  const [showQuestionDialog, setShowQuestionDialog] = useState(false)
  const [showSavedQuizzesDialog, setShowSavedQuizzesDialog] = useState(false)
  const [savedQuizzes, setSavedQuizzes] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Question dialog state
  const [currentQuestion, setCurrentQuestion] = useState({
    question: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    optionE: '',
    correctAnswer: ''
  })

  useEffect(() => {
    if (user) {
      loadTeacherClasses()
      loadSavedQuizzes()
    }
  }, [user])

  const loadTeacherClasses = async () => {
    try {
      const classesRef = collection(db, 'classes')
      const q = query(classesRef, where('teacherId', '==', user.uid))
      const querySnapshot = await getDocs(q)
      
      const classesData = await Promise.all(querySnapshot.docs.map(async (doc) => {
        const classData = { id: doc.id, ...doc.data() }
        
        // Her sınıf için öğrenci sayısını hesapla
        try {
          const studentsRef = collection(db, 'users')
          const studentsQuery = query(
            studentsRef,
            where('role', '==', 'student'),
            where('classIds', 'array-contains', doc.id)
          )
          const studentsSnapshot = await getDocs(studentsQuery)
          classData.studentCount = studentsSnapshot.size
        } catch (error) {
          console.error('Error counting students for class:', doc.id, error)
          classData.studentCount = 0
        }
        
        return classData
      }))
      
      setTeacherClasses(classesData)
    } catch (error) {
      console.error('Error loading classes:', error)
      toast.error('Sınıflar yüklenirken hata oluştu')
    }
  }

  const loadSavedQuizzes = async () => {
    try {
      const savedQuizzesRef = collection(db, 'teachers', user.uid, 'savedQuizzes')
      const q = query(savedQuizzesRef, orderBy('createdAt', 'desc'))
      const querySnapshot = await getDocs(q)
      
      const quizzesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setSavedQuizzes(quizzesData)
    } catch (error) {
      console.error('Error loading saved quizzes:', error)
    }
  }

  const handleAddQuestion = () => {
    setCurrentQuestion({
      question: '',
      optionA: '',
      optionB: '',
      optionC: '',
      optionD: '',
      optionE: '',
      correctAnswer: ''
    })
    setShowQuestionDialog(true)
  }

  const handleSaveQuestion = () => {
    if (!currentQuestion.question || !currentQuestion.optionA || !currentQuestion.optionB || 
        !currentQuestion.optionC || !currentQuestion.optionD || !currentQuestion.optionE || 
        !currentQuestion.correctAnswer) {
      toast.error('Tüm alanları doldurun')
      return
    }

    setQuestions([...questions, { ...currentQuestion, id: Date.now() }])
    setShowQuestionDialog(false)
    toast.success('Soru eklendi')
  }

  const handleDeleteQuestion = (questionId) => {
    setQuestions(questions.filter(q => q.id !== questionId))
    toast.success('Soru silindi')
  }

  const handleSelectClass = (classData) => {
    setSelectedClassId(classData.id)
    setSelectedClassName(classData.className)
    setShowClassDialog(false)
    loadStudentsForClass(classData.id)
  }

  const loadStudentsForClass = async (classId) => {
    try {
      const studentsRef = collection(db, 'users')
      const q = query(
        studentsRef,
        where('role', '==', 'student'),
        where('classIds', 'array-contains', classId)
      )
      const querySnapshot = await getDocs(q)
      
      const studentsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setStudents(studentsData)
      setShowStudentDialog(true)
    } catch (error) {
      console.error('Error loading students:', error)
      toast.error('Öğrenciler yüklenirken hata oluştu')
    }
  }

  const handleSelectAllStudents = () => {
    const allStudentIds = students.map(s => s.id)
    const allStudentNames = students.map(s => s.username || s.email)
    setSelectedStudentIds(allStudentIds)
    setSelectedStudentNames(allStudentNames)
    setIsIndividualSelection(false)
    setShowStudentDialog(false)
    toast.success(`Tüm sınıf seçildi (${allStudentIds.length} öğrenci)`)
  }

  const handleSelectIndividualStudents = () => {
    setIsIndividualSelection(true)
    setSelectedStudentIds([])
    setSelectedStudentNames([])
  }

  const handleStudentToggle = (student) => {
    const isSelected = selectedStudentIds.includes(student.id)
    if (isSelected) {
      setSelectedStudentIds(selectedStudentIds.filter(id => id !== student.id))
      setSelectedStudentNames(selectedStudentNames.filter(name => name !== (student.username || student.email)))
    } else {
      setSelectedStudentIds([...selectedStudentIds, student.id])
      setSelectedStudentNames([...selectedStudentNames, student.username || student.email])
    }
  }

  const handleCreateQuiz = async () => {
    if (!quizTitle.trim()) {
      toast.error('Quiz başlığı girin')
      return
    }

    if (questions.length === 0) {
      toast.error('En az bir soru ekleyin')
      return
    }

    setSubmitting(true)
    try {
      const quizData = {
        teacherId: user.uid,
        title: quizTitle.trim(),
        description: quizDescription.trim(),
        timeLimit: timeLimit ? parseInt(timeLimit) : 0,
        createdDate: new Date(),
        studentIds: selectedStudentIds,
        studentNames: selectedStudentNames,
        unlimited: unlimited,
        dueDate: dueDate && !unlimited ? dueDate : null,
        classId: selectedClassId,
        className: selectedClassName,
        isIndividualSelection: isIndividualSelection,
        questions: questions.map(q => ({
          question: q.question,
          optionA: q.optionA,
          optionB: q.optionB,
          optionC: q.optionC,
          optionD: q.optionD,
          optionE: q.optionE,
          correctAnswer: q.correctAnswer
        }))
      }

      await addDoc(collection(db, 'miniQuizzes'), quizData)
      
      toast.success('Quiz başarıyla oluşturuldu ve gönderildi!')
      onBack()
    } catch (error) {
      console.error('Error creating quiz:', error)
      toast.error('Quiz oluşturulurken hata oluştu')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSaveQuiz = async () => {
    if (!quizTitle.trim()) {
      toast.error('Quiz başlığı boş olamaz')
      return
    }

    if (questions.length === 0) {
      toast.error('En az bir soru eklemelisiniz')
      return
    }

    try {
      const quizData = {
        title: quizTitle.trim(),
        description: quizDescription.trim(),
        timeLimit: timeLimit,
        unlimited: unlimited,
        dueDate: dueDate,
        questions: questions,
        createdAt: new Date()
      }

      await addDoc(collection(db, 'teachers', user.uid, 'savedQuizzes'), quizData)
      toast.success('Quiz başarıyla kaydedildi')
      loadSavedQuizzes()
    } catch (error) {
      console.error('Error saving quiz:', error)
      toast.error('Quiz kaydedilirken hata oluştu')
    }
  }

  const handleLoadSavedQuiz = (quiz) => {
    setQuizTitle(quiz.title || '')
    setQuizDescription(quiz.description || '')
    setTimeLimit(quiz.timeLimit || '')
    setUnlimited(quiz.unlimited || true)
    setDueDate(quiz.dueDate || null)
    setQuestions(quiz.questions || [])
    setShowSavedQuizzesDialog(false)
    toast.success('Quiz yüklendi')
  }

  const clearSelectedStudents = () => {
    setSelectedStudentIds([])
    setSelectedStudentNames([])
    setSelectedClassId(null)
    setSelectedClassName(null)
    setIsIndividualSelection(false)
    toast.success('Seçilen öğrenciler temizlendi')
  }

  const clearSelectedClass = () => {
    setSelectedClassId(null)
    setSelectedClassName(null)
    setSelectedStudentIds([])
    setSelectedStudentNames([])
    setIsIndividualSelection(false)
    toast.success('Seçilen sınıf temizlendi')
  }

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
      {/* AppBar */}
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Mini Quiz Oluştur
          </Typography>
          <IconButton color="inherit" onClick={handleSaveQuiz}>
            <SaveIcon />
          </IconButton>
          <IconButton color="inherit" onClick={() => setShowSavedQuizzesDialog(true)}>
            <FolderIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* Quiz Bilgileri */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
              Quiz Bilgileri
            </Typography>
            
            <TextField
              fullWidth
              label="Quiz başlığı"
              value={quizTitle}
              onChange={(e) => setQuizTitle(e.target.value)}
              placeholder="Quiz başlığını girin"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Quiz açıklaması"
              value={quizDescription}
              onChange={(e) => setQuizDescription(e.target.value)}
              placeholder="Quiz açıklamasını girin"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Süre sınırı (dakika)"
              type="number"
              value={timeLimit}
              onChange={(e) => setTimeLimit(e.target.value)}
              placeholder="Süre sınırı yoksa boş bırakın"
              sx={{ mb: 2 }}
            />

                         <FormControlLabel
               control={
                 <Checkbox
                   checked={unlimited}
                   onChange={(e) => setUnlimited(e.target.checked)}
                 />
               }
               label="Süresiz"
             />

             {!unlimited && (
               <Box sx={{ mt: 2 }}>
                 <Typography variant="subtitle2" gutterBottom>
                   Bitiş Tarihi
                 </Typography>
                 <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
                   <DatePicker
                     value={dueDate}
                     onChange={(newValue) => setDueDate(newValue)}
                     renderInput={(params) => (
                       <TextField
                         {...params}
                         fullWidth
                         placeholder="Bitiş tarihi seçin"
                         InputProps={{
                           ...params.InputProps,
                           startAdornment: <CalendarIcon sx={{ mr: 1, color: 'action.active' }} />
                         }}
                       />
                     )}
                     minDate={new Date()}
                     format="dd/MM/yyyy"
                   />
                 </LocalizationProvider>
               </Box>
             )}
          </CardContent>
        </Card>

        {/* Sınıf ve Öğrenci Seçimi */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
              Sınıf ve Öğrenci Seçimi
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setShowClassDialog(true)}
                startIcon={<AddIcon />}
              >
                {selectedClassName ? `Seçili: ${selectedClassName}` : 'Sınıf Seç'}
              </Button>
              {selectedClassName && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={clearSelectedClass}
                  title="Sınıf Seçimini Temizle"
                >
                  <ClearIcon />
                </IconButton>
              )}
            </Box>

            {selectedStudentNames.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2">
                    Seçili Öğrenciler ({selectedStudentNames.length}):
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={clearSelectedStudents}
                    title="Öğrenci Seçimini Temizle"
                  >
                    <ClearIcon />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedStudentNames.map((name, index) => (
                    <Chip key={index} label={name} size="small" />
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Sorular */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" color="primary">
                Sorular ({questions.length})
              </Typography>
              <Button
                variant="contained"
                onClick={handleAddQuestion}
                startIcon={<AddIcon />}
              >
                Soru Ekle
              </Button>
            </Box>

            {questions.length === 0 ? (
              <Alert severity="info">
                Henüz soru eklenmemiş. Quiz oluşturmak için en az bir soru ekleyin.
              </Alert>
            ) : (
              <List>
                {questions.map((question, index) => (
                  <ListItem key={question.id} sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, mb: 1 }}>
                    <ListItemText
                      primary={`Soru ${index + 1}: ${question.question}`}
                      secondary={`Doğru Cevap: ${question.correctAnswer}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteQuestion(question.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>

        {/* Quiz Oluştur Butonu */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleCreateQuiz}
          disabled={submitting || questions.length === 0}
          startIcon={submitting ? <CircularProgress size={20} /> : <QuizIcon />}
          sx={{ mb: 2 }}
        >
          {submitting ? 'Gönderiliyor...' : 'Quiz Oluştur'}
        </Button>
      </Container>

      {/* Soru Ekleme Dialog */}
      <Dialog
        open={showQuestionDialog}
        onClose={() => setShowQuestionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Soru Ekle</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Soru"
            value={currentQuestion.question}
            onChange={(e) => setCurrentQuestion({...currentQuestion, question: e.target.value})}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="A) Seçenek"
                value={currentQuestion.optionA}
                onChange={(e) => setCurrentQuestion({...currentQuestion, optionA: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="B) Seçenek"
                value={currentQuestion.optionB}
                onChange={(e) => setCurrentQuestion({...currentQuestion, optionB: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="C) Seçenek"
                value={currentQuestion.optionC}
                onChange={(e) => setCurrentQuestion({...currentQuestion, optionC: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="D) Seçenek"
                value={currentQuestion.optionD}
                onChange={(e) => setCurrentQuestion({...currentQuestion, optionD: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="E) Seçenek"
                value={currentQuestion.optionE}
                onChange={(e) => setCurrentQuestion({...currentQuestion, optionE: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Doğru Cevap (A/B/C/D/E)"
                value={currentQuestion.correctAnswer}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase()
                  if (['A', 'B', 'C', 'D', 'E'].includes(value) || value === '') {
                    setCurrentQuestion({...currentQuestion, correctAnswer: value})
                  }
                }}
                inputProps={{ maxLength: 1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowQuestionDialog(false)}>İptal</Button>
          <Button onClick={handleSaveQuestion} variant="contained">Ekle</Button>
        </DialogActions>
      </Dialog>

      {/* Sınıf Seçimi Dialog */}
      <Dialog
        open={showClassDialog}
        onClose={() => setShowClassDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Sınıf Seç</DialogTitle>
        <DialogContent>
          {teacherClasses.length === 0 ? (
            <Alert severity="info">
              Henüz sınıfınız bulunmuyor. Önce sınıf oluşturun.
            </Alert>
          ) : (
            <List>
              {teacherClasses.map((classData) => (
                <ListItem
                  key={classData.id}
                  button
                  onClick={() => handleSelectClass(classData)}
                >
                  <ListItemText
                    primary={classData.className}
                    secondary={`${classData.studentCount || 0} öğrenci`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowClassDialog(false)}>İptal</Button>
        </DialogActions>
      </Dialog>

      {/* Öğrenci Seçimi Dialog */}
      <Dialog
        open={showStudentDialog}
        onClose={() => setShowStudentDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Öğrenci Seçimi</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Button
                variant="contained"
                onClick={handleSelectAllStudents}
                sx={{ mr: 1 }}
              >
                Tüm Sınıfı Seç
              </Button>
              <Button
                variant="outlined"
                onClick={handleSelectIndividualStudents}
              >
                Bireysel Seçim
              </Button>
            </Box>
            {selectedStudentIds.length > 0 && (
              <IconButton
                size="small"
                color="error"
                onClick={clearSelectedStudents}
                title="Seçimi Temizle"
              >
                <ClearIcon />
              </IconButton>
            )}
          </Box>

          {isIndividualSelection && (
            <List>
              {students.map((student) => (
                <ListItem
                  key={student.id}
                  button
                  onClick={() => handleStudentToggle(student)}
                >
                  <ListItemText
                    primary={student.username || student.email}
                  />
                  <Checkbox
                    checked={selectedStudentIds.includes(student.id)}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStudentDialog(false)}>İptal</Button>
          {isIndividualSelection && (
            <Button 
              onClick={() => setShowStudentDialog(false)}
              variant="contained"
            >
              Seçimi Tamamla ({selectedStudentIds.length} öğrenci)
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Kayıtlı Quizler Dialog */}
      <Dialog
        open={showSavedQuizzesDialog}
        onClose={() => setShowSavedQuizzesDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Kayıtlı Quizler</DialogTitle>
        <DialogContent>
          {savedQuizzes.length === 0 ? (
            <Alert severity="info">
              Henüz kayıtlı quiz bulunmuyor.
            </Alert>
          ) : (
            <List>
              {savedQuizzes.map((quiz) => (
                <ListItem
                  key={quiz.id}
                  button
                  onClick={() => handleLoadSavedQuiz(quiz)}
                >
                  <ListItemText
                    primary={quiz.title}
                    secondary={`${quiz.questions?.length || 0} soru`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSavedQuizzesDialog(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>
      </Box>
    </SpaceTheme>
  )
}

export default MiniQuizCreate
